<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Budget Justification</h3>
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Please provide an explanation for why each of the line item expenses is necessary in accomplishing your goal.</p>

            <div v-if="isLoading" class="flex justify-center items-center h-[200px]">
                <ActivityIndicator />
            </div>

            <!-- Questions Content -->
            <div v-else class="space-y-6">
                <div v-for="(question, qIndex) in questions" :key="qIndex" class="w-full">
                    <QuestionField
                        v-model="answers[qIndex]"
                        :label="question.label"
                        :placeholder="question.placeholder"
                        :error-message="errorMessages[qIndex] || (v$.currentAnswers[qIndex].$error ? v$.currentAnswers[qIndex].$errors[0]?.$message : '')"
                        class="budget-justification-field"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import QuestionField from "@/components/QuestionField.vue";
import ActivityIndicator from "@/components/ActivityIndicator.vue";
import { useAuthStore } from "@/stores/auth";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import global from "@/global";
import { useStepValidation } from "@/composables/useStepValidation";

const isLoading = ref(true);
const errorMessages = ref([]);
const answers = ref([]);
const lastSavedValues = ref([]);
const questions = ref([]);
const categoryIds = ref([]);
const questionIds = ref([]);

// Validation rules for all questions
const rules = {
    currentAnswers: computed(() => questions.value.map(() => ({
        required: helpers.withMessage("Answer is required!", required)
    })))
};

const v$ = useVuelidate(rules, {
    currentAnswers: computed(() => answers.value || questions.value.map(() => ''))
});

// Fetching the auth store
const authStore = useAuthStore();

// Fetching question data
async function fetchQuestionData() {
    try {
        const response = await fetch(`${global.API_URL}/questions/budget_justification`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();

        // Check if we have a valid response with questions
        if (!data.questions || !Array.isArray(data.questions) || data.questions.length === 0) {
            throw new Error('No questions found in the response');
        }

        // Map questions with the new structure
        questions.value = data.questions.map(question => ({
            id: question.id,
            label: question.label,
            placeholder: `Enter your answer for...`,
            question_name: question.question_name,
            question_type: question.question_type,
            category_id: question.category_id
        }));

        // Map category IDs and question IDs
        categoryIds.value = questions.value.map(q => q.category_id);
        questionIds.value = questions.value.map(q => q.id);

        // Initialize answers array with the correct number of questions
        answers.value = questions.value.map(() => "");
        lastSavedValues.value = questions.value.map(() => "");
        errorMessages.value = questions.value.map(() => "");

        // Fetch existing answers after questions are loaded
        await getUserAnswers();
    } catch (error) {
        errorMessages.value = [error.message || "Failed to load question data. Please try again."];
        questions.value = [];
        categoryIds.value = [];
        questionIds.value = [];
        answers.value = [];
        lastSavedValues.value = [];
    }
}

// Custom validation function for the useStepValidation composable
const validateStep = async () => {
    await v$.value.$validate();

    if (v$.value.$invalid) {
        return false;
    }

    try {
        const success = await saveAllAnswers();
        if (!success) {
            errorMessages.value = ["Please ensure all answers are saved before proceeding."];
            return false;
        }
        return true;
    } catch (error) {
        console.error("Failed to save answers:", error);
        errorMessages.value = ["Please ensure your answers are saved before proceeding."];
        return false;
    }
};

// Use the step validation composable
useStepValidation({
    initialValidState: false,
    validateFn: validateStep,
});

// Fetching user answers
async function getUserAnswers() {
    try {
        await Promise.all(questions.value.map(async (question, qIndex) => {
            const res = await fetch(
                `${global.API_URL}/answers?question_id=${question.id}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${authStore.user.accessToken}`,
                    },
                }
            );

            if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

            const data = await res.json();
            if (data.answer?.answer) {
                answers.value[qIndex] = data.answer.answer;
                lastSavedValues.value[qIndex] = data.answer.answer;
            }
        }));
    } catch (error) {
        console.error("Failed to fetch user answers:", error);
        errorMessages.value = ["Failed to load your previous answers. You can still create new ones."];
    }
}

// Save all answers
async function saveAllAnswers() {
    try {
        const savePromises = questions.value.map(async (question, qIndex) => {
            if (answers.value[qIndex] !== lastSavedValues.value[qIndex]) {
                const res = await fetch(`${global.API_URL}/answers`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${authStore.user.accessToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        category_id: question.category_id,
                        question_id: question.id,
                        answer: answers.value[qIndex],
                    }),
                });

                if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

                const data = await res.json();
                if (data.status) {
                    lastSavedValues.value[qIndex] = answers.value[qIndex];
                    return true;
                } else {
                    throw new Error("Failed to save answer");
                }
            }
            return true;
        });

        const results = await Promise.all(savePromises);
        return results.every(result => result === true);
    } catch (error) {
        errorMessages.value = ["Failed to save answers. Please try again."];
        return false;
    }
}

// Watch for changes in answers and save automatically
watch(answers, async (newAnswers, oldAnswers) => {
    if (newAnswers.length !== oldAnswers.length) return; // Skip if arrays are different lengths
    
    const changedIndex = newAnswers.findIndex((answer, index) => answer !== oldAnswers[index]);
    if (changedIndex !== -1) {
        try {
            const success = await saveAllAnswers();
            if (!success) {
                errorMessages.value[changedIndex] = "Failed to save answer. Please try again.";
            }
        } catch (error) {
            errorMessages.value[changedIndex] = "Failed to save answer. Please try again.";
        }
    }
}, { deep: true });

onMounted(async () => {
    try {
        isLoading.value = true;
        errorMessages.value = [];

        await authStore.fetchUserData();

        if (!authStore.user?.organization_id) {
            errorMessages.value = ["Unable to load organization data. Please try refreshing the page."];
            console.error("No organization ID available");
            return;
        }

        // First fetch the questions
        await fetchQuestionData();

        if (questions.value.length === 0) {
            errorMessages.value = ["Unable to load questions. Please try refreshing the page."];
            console.error("No questions available");
            return;
        }
    } catch (error) {
        console.error("Error during component initialization:", error);
        errorMessages.value = ["An error occurred while loading the data. Please try refreshing the page."];
    } finally {
        isLoading.value = false;
    }
});
</script>

<style scoped>
.page-title {
    font-weight: 600;
    font-size: 20px;
    line-height: 140%;
    color: #414141;
    margin-bottom: 24px;
}

/* Add styles for centering label in BudgetJustificationView only */
:deep(.budget-justification-field .question-label) {
    text-align: center;
}
</style> 